import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import "../styles/institution.css";
import { AppShell } from "../components/AppShell";
import { EmptyState } from "../components/EmptyState";
import { PaginationBar, usePagination } from "../components/Pagination";
import { useAuth } from "../hooks/useAuth";
import { useT } from "../i18n/LanguageProvider";
import {
  listInstitutionRequests,
  listOrganisationRequests,
  approveInstitutionRequest,
  approveOrganisationRequest,
  markEmploymentEnded,
  rejectInstitutionRequest,
  rejectOrganisationRequest,
} from "../api/verifications";
import { createDownloadUrl } from "../api/storage";
import { ExportRangeDialog } from "../components/ExportRangeDialog";
import { MessageThreadDialog } from "../components/MessageThreadDialog";
import { C, font } from "../styles/tokens";
import { InlineIcon } from "../components/InlineIcon";

const TABS = ["ALL", "PENDING", "VERIFIED", "REJECTED"];

const SUGGESTED_REJECTION_REASONS = [
  "The certificate doesn't match our records.",
  "Dates of attendance don't match our records.",
  "Cannot verify; required documents not submitted.",
  "Field of study doesn't match institutional records.",
];

export function VerificationInboxPage() {
  const { localUser } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const isInstitution = localUser?.type === "INSTITUTION_OFFICER";
  const isOrganisation = localUser?.type === "ORGANISATION_OFFICER";

  const getInitialTab = () => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get("tab") || location.state?.tab;
    if (tabParam && TABS.includes(tabParam.toUpperCase())) {
      return tabParam.toUpperCase();
    }
    return "ALL";
  };

  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(getInitialTab);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get("tab") || location.state?.tab;
    if (tabParam && TABS.includes(tabParam.toUpperCase())) {
      setActiveTab(tabParam.toUpperCase());
    }
  }, [location.search, location.state]);

  // Selected item for detail view / decision modal
  const [selectedReq, setSelectedReq] = useState(null);
  // Decision dialog: { req, mode } when open.
  const [decision, setDecision] = useState(null);
  const [reason, setReason] = useState("");
  const [deciding, setDeciding] = useState(false);
  const [decideError, setDecideError] = useState(null);

  // Export dialog
  const [exportOpen, setExportOpen] = useState(false);

  // Message thread dialog
  const [threadReq, setThreadReq] = useState(null);

  // Mark employment ended dialog
  const [markEnded, setMarkEnded] = useState(null);
  const [endDateInput, setEndDateInput] = useState("");
  const [markBusy, setMarkBusy] = useState(false);
  const [markError, setMarkError] = useState(null);

  const listFn = isInstitution
    ? listInstitutionRequests
    : listOrganisationRequests;

  const loadAll = useCallback(async () => {
    if (!isInstitution && !isOrganisation) return;
    setLoading(true);
    setError(null);
    try {
      const [pending, verified, rejected] = await Promise.all([
        listFn("PENDING"),
        listFn("VERIFIED"),
        listFn("REJECTED"),
      ]);
      const merged = [...pending, ...verified, ...rejected].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
      setAllItems(merged);
    } catch (err) {
      setError(err.message ?? t("common.somethingWrong"));
    } finally {
      setLoading(false);
    }
  }, [isInstitution, isOrganisation]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Handle deep-linking from notifications (?request=reqId or ?thread=reqId)
  useEffect(() => {
    if (allItems.length === 0) return;
    const searchParams = new URLSearchParams(location.search);
    const reqId = searchParams.get("request");
    if (reqId) {
      const target = allItems.find((it) => it.id === reqId);
      if (target) {
        setSelectedReq(target);
      }
    }
    const threadId = searchParams.get("thread");
    if (threadId) {
      const target = allItems.find((it) => it.id === threadId);
      if (target) {
        setThreadReq(target);
      }
    }
  }, [allItems, location.search]);

  // Open the certificate in a new tab
  async function openCertificate(req) {
    const key = req?.credential?.certificateS3Key;
    if (!key) return;
    const win = window.open("", "_blank", "noopener");
    try {
      const { downloadUrl } = await createDownloadUrl(key);
      if (win) win.location = downloadUrl;
      else window.open(downloadUrl, "_blank", "noopener");
    } catch {
      if (win) win.close();
    }
  }

  function openDecision(req, mode) {
    setDecision({ req, mode });
    setReason("");
    setDecideError(null);
    setDeciding(false);
  }

  function closeDecision() {
    setDecision(null);
    setReason("");
    setDecideError(null);
    setDeciding(false);
  }

  async function confirmDecision() {
    if (!decision) return;
    const { req, mode } = decision;
    if (mode === "reject" && !reason.trim()) {
      setDecideError("A rejection reason is required.");
      return;
    }
    setDeciding(true);
    setDecideError(null);
    try {
      const approveFn = isInstitution
        ? approveInstitutionRequest
        : approveOrganisationRequest;
      const rejectFn = isInstitution
        ? rejectInstitutionRequest
        : rejectOrganisationRequest;
      if (mode === "approve") {
        await approveFn(req.id, reason.trim() || undefined);
      } else {
        await rejectFn(req.id, reason.trim());
      }

      const newStatus = mode === "approve" ? "VERIFIED" : "REJECTED";
      const decidedAt = new Date().toISOString();
      setAllItems((prev) =>
        prev.map((it) =>
          it.id === req.id
            ? {
                ...it,
                status: newStatus,
                decidedAt,
                decidedBy: localUser,
                decisionReason: reason.trim() || null,
              }
            : it,
        ),
      );
      if (selectedReq?.id === req.id) {
        setSelectedReq((prev) =>
          prev
            ? { ...prev, status: newStatus, decidedAt, decidedBy: localUser }
            : null,
        );
      }
      closeDecision();
    } catch (err) {
      setDecideError(err.message ?? t("common.somethingWrong"));
      setDeciding(false);
    }
  }

  function handleTabChange(tab) {
    setActiveTab(tab);
    pager.reset();
  }

  const filtered =
    activeTab === "ALL"
      ? allItems
      : allItems.filter((i) => i.status === activeTab);

  const pager = usePagination(filtered, 12);

  const counts = {
    ALL: allItems.length,
    PENDING: allItems.filter((i) => i.status === "PENDING").length,
    VERIFIED: allItems.filter((i) => i.status === "VERIFIED").length,
    REJECTED: allItems.filter((i) => i.status === "REJECTED").length,
  };

  if (!isInstitution && !isOrganisation) {
    return (
      <AppShell>
        <div
          style={{
            background: "#ffffff",
            padding: 24,
            borderRadius: 12,
            border: "1px solid #e2e8f0",
          }}
        >
          This page is for institution and organisation officers.
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell maxWidth="100%">
      <div style={{ width: "100%", paddingBottom: 40 }}>
        {/* Header Breadcrumbs & Titles */}
        <div style={{ marginBottom: 24 }}>
          <div
            style={{
              fontSize: 13,
              color: "#64748b",
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginBottom: 8,
            }}
          >
            <span
              onClick={() => navigate("/dashboard")}
              style={{ cursor: "pointer", color: "#64748b" }}
            >
              Home
            </span>
            <span style={{ color: "#cbd5e1" }}>/</span>
            <span style={{ color: "#0f172a", fontWeight: 600 }}>Inbox</span>
          </div>

          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#2563eb",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              marginBottom: 4,
            }}
          >
            COMMUNICATION CENTER
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
            }}
          >
            <div>
              <h1
                style={{
                  fontSize: 32,
                  fontWeight: 800,
                  color: "#0f172a",
                  margin: 0,
                  letterSpacing: "-0.02em",
                }}
              >
                Inbox
              </h1>
              <p style={{ fontSize: 14, color: "#64748b", margin: "4px 0 0" }}>
                Stay on top of conversations across your institution.
              </p>
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => setExportOpen(true)}
                disabled={counts.VERIFIED === 0}
                style={{
                  padding: "8px 16px",
                  fontSize: 13,
                  fontWeight: 600,
                  color: counts.VERIFIED === 0 ? "#94a3b8" : "#16a34a",
                  background: "#ffffff",
                  border: `1px solid ${counts.VERIFIED === 0 ? "#e2e8f0" : "#bbf7d0"}`,
                  borderRadius: 8,
                  cursor: counts.VERIFIED === 0 ? "not-allowed" : "pointer",
                  transition: "all 0.15s ease",
                }}
              >
                Download Excel ({counts.VERIFIED})
              </button>
              <button
                onClick={loadAll}
                style={{
                  padding: "8px 16px",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#334155",
                  background: "#ffffff",
                  border: "1px solid #cbd5e1",
                  borderRadius: 8,
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
              >
                Refresh Data
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div
            style={{
              background: "#fef2f2",
              color: "#dc2626",
              border: "1px solid #fecaca",
              padding: 14,
              borderRadius: 10,
              marginBottom: 20,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {/* Card Container (exact UI matching screenshot) */}
        <div
          style={{
            background: "#ffffff",
            border: "1.5px solid #3b82f6",
            borderRadius: 16,
            boxShadow: "0 4px 20px rgba(59, 130, 246, 0.08)",
            padding: "24px 28px",
          }}
        >
          {/* Card Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: 20,
            }}
          >
            <div>
              <h2
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: "#0f172a",
                  margin: 0,
                  letterSpacing: "-0.01em",
                }}
              >
                Verification Log
              </h2>
              <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
                Review applications and their latest verification decision.
              </div>
            </div>

            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 13,
                fontWeight: 600,
                color: "#475569",
                background: "#f8fafc",
                padding: "6px 14px",
                borderRadius: 20,
                border: "1px solid #e2e8f0",
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: "#64748b",
                }}
              />
              {counts.ALL} applications
            </div>
          </div>

          {/* Filter Tabs Row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 28,
              borderBottom: "1px solid #e2e8f0",
              paddingBottom: 2,
              marginBottom: 24,
            }}
          >
            {TABS.map((tabKey) => {
              const label =
                tabKey === "ALL"
                  ? "All"
                  : tabKey === "PENDING"
                    ? "Pending"
                    : tabKey === "VERIFIED"
                      ? "Verified"
                      : "Rejected";
              const isActive = activeTab === tabKey;
              return (
                <button
                  key={tabKey}
                  onClick={() => handleTabChange(tabKey)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: "10px 0 12px",
                    fontSize: 14,
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? "#2563eb" : "#64748b",
                    borderBottom: isActive
                      ? "2px solid #2563eb"
                      : "2px solid transparent",
                    marginBottom: -3,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    transition: "all 0.15s ease",
                  }}
                >
                  <span>{label}</span>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: isActive ? 700 : 500,
                      color: isActive ? "#2563eb" : "#64748b",
                    }}
                  >
                    {counts[tabKey]}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Active Section Sub-heading */}
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: "#0f172a",
              marginBottom: 16,
            }}
          >
            {activeTab === "ALL"
              ? "All applications"
              : activeTab === "PENDING"
                ? "Pending applications"
                : activeTab === "VERIFIED"
                  ? "Verified applications"
                  : "Rejected applications"}
          </div>

          {/* Loading or Empty State or List */}
          {loading ? (
            <div
              style={{
                padding: "40px 0",
                textAlign: "center",
                fontSize: 14,
                color: "#64748b",
              }}
            >
              Loading real-time application records...
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: "40px 0", textAlign: "center" }}>
              <EmptyState
                kind={activeTab === "PENDING" ? "inbox" : "check"}
                title={
                  activeTab === "ALL"
                    ? "No verification requests found"
                    : `No ${activeTab.toLowerCase()} requests`
                }
                body={
                  activeTab === "PENDING"
                    ? "Applications submitted for verification will appear here in real time."
                    : undefined
                }
              />
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {pager.pageItems.map((req) => (
                <VerificationItemRow
                  key={req.id}
                  req={req}
                  onClick={() => setSelectedReq(req)}
                />
              ))}
            </div>
          )}

          {/* Pagination Bar */}
          <div
            style={{ marginTop: 24, marginHorizontal: -28, marginBottom: -24 }}
          >
            <PaginationBar
              pager={pager}
              itemLabel="applications"
              alwaysShow={true}
            />
          </div>
        </div>
      </div>

      {/* Item Detail / Inspection Modal */}
      {selectedReq && (
        <ItemDetailModal
          req={selectedReq}
          onClose={() => setSelectedReq(null)}
          onOpenFile={() => openCertificate(selectedReq)}
          onApprove={() => openDecision(selectedReq, "approve")}
          onReject={() => openDecision(selectedReq, "reject")}
          onOpenThread={() => setThreadReq(selectedReq)}
          onMarkEnded={
            isOrganisation
              ? () => {
                  setMarkEnded(selectedReq);
                  setEndDateInput("");
                  setMarkError(null);
                }
              : undefined
          }
        />
      )}

      {/* Approve / Reject Dialog */}
      {decision && (
        <DecisionDialog
          decision={decision}
          reason={reason}
          setReason={setReason}
          deciding={deciding}
          error={decideError}
          onCancel={closeDecision}
          onConfirm={confirmDecision}
          t={t}
        />
      )}

      {/* Message Thread */}
      {threadReq && (
        <MessageThreadDialog
          requestId={threadReq.id}
          title={`${threadReq.credential?.title ?? "Credential"} - ${[
            threadReq.professional?.firstName,
            threadReq.professional?.lastName,
          ]
            .filter(Boolean)
            .join(" ")}`}
          onClose={() => setThreadReq(null)}
        />
      )}

      {/* Mark Employment Ended */}
      {markEnded && (
        <MarkEndedDialog
          req={markEnded}
          endDate={endDateInput}
          setEndDate={setEndDateInput}
          busy={markBusy}
          error={markError}
          onCancel={() => setMarkEnded(null)}
          onConfirm={async () => {
            if (!endDateInput) {
              setMarkError("Pick the employee's last working day.");
              return;
            }
            setMarkBusy(true);
            setMarkError(null);
            try {
              await markEmploymentEnded(markEnded.id, endDateInput);
              setMarkEnded(null);
              await loadAll();
            } catch (err) {
              setMarkError(
                err?.response?.data?.message ??
                  err?.message ??
                  "Failed to mark as ended.",
              );
            } finally {
              setMarkBusy(false);
            }
          }}
        />
      )}

      {/* Excel Export */}
      <ExportRangeDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        items={allItems}
      />
    </AppShell>
  );
}

/* =============================================================================
   VerificationItemRow - Exact single card row matching design screenshot
   ============================================================================= */

function VerificationItemRow({ req, onClick }) {
  const [hover, setHover] = useState(false);
  const fullName =
    `${req.professional?.firstName ?? ""} ${req.professional?.lastName ?? ""}`.trim() ||
    req.user?.fullName ||
    "Applicant";
  const initials = getInitials(fullName);
  const appCode = `#APP-${req.id ? req.id.toString().slice(-4).toUpperCase() : "1000"}`;
  const credTitle =
    req.credential?.title ||
    req.title ||
    req.credentialTitle ||
    "Academic records";
  const metaText = formatInboxItemMeta(req);

  const isPending = req.status === "PENDING";
  const isVerified = req.status === "VERIFIED";
  const isRejected = req.status === "REJECTED";

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "16px 20px",
        borderRadius: 14,
        background: hover ? "#f8fafc" : "#fafafa",
        border: hover ? "1.5px solid #cbd5e1" : "1px solid #f1f5f9",
        cursor: "pointer",
        transition: "all 0.15s ease",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        {/* Avatar Initials */}
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            background: "#eff6ff",
            color: "#2563eb",
            fontSize: 14,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {initials}
        </div>

        {/* Text details */}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
              {fullName}
            </span>
            <span style={{ fontSize: 13, fontWeight: 500, color: "#94a3b8" }}>
              {appCode}
            </span>
          </div>

          <div style={{ fontSize: 13, color: "#64748b", marginTop: 3 }}>
            {credTitle} - <span style={{ color: "#94a3b8" }}>{metaText}</span>
          </div>
        </div>
      </div>

      {/* Status Pill Badge & Right Chevron */}
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        {isPending && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 14px",
              borderRadius: 20,
              background: "#fffbeb",
              color: "#b45309",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "#d97706",
              }}
            />
            Pending
          </span>
        )}

        {isVerified && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 14px",
              borderRadius: 20,
              background: "#f0fdf4",
              color: "#16a34a",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "#16a34a",
              }}
            />
            Verified
          </span>
        )}

        {isRejected && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 14px",
              borderRadius: 20,
              background: "#fef2f2",
              color: "#dc2626",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "#dc2626",
              }}
            />
            Rejected
          </span>
        )}

        <span style={{ fontSize: 16, color: "#cbd5e1", fontWeight: 700 }}>
          <InlineIcon name="chevronRight" size={16} />
        </span>
      </div>
    </div>
  );
}

/* =============================================================================
   Item Detail Inspection Modal
   ============================================================================= */

function ItemDetailModal({
  req,
  onClose,
  onOpenFile,
  onApprove,
  onReject,
  onOpenThread,
  onMarkEnded,
}) {
  const fullName =
    `${req.professional?.firstName ?? ""} ${req.professional?.lastName ?? ""}`.trim() ||
    req.user?.fullName ||
    "Applicant";
  const cred = req.credential ?? {};
  const isPending = req.status === "PENDING";
  const hasCert = !!cred.certificateS3Key;
  const metaPairs = metadataPairs(cred.metadata);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(15, 23, 42, 0.5)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 620,
          background: "#ffffff",
          borderRadius: 16,
          boxShadow: "0 20px 50px rgba(15, 23, 42, 0.2)",
          fontFamily: font,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "#eff6ff",
                color: "#2563eb",
                fontWeight: 700,
                fontSize: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {getInitials(fullName)}
            </div>
            <div>
              <h3
                style={{
                  fontSize: 17,
                  fontWeight: 700,
                  color: "#0f172a",
                  margin: 0,
                }}
              >
                {fullName}
              </h3>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                {req.professional?.user?.email ||
                  "Applicant Verification Request"}
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "none",
              border: "none",
              fontSize: 20,
              color: "#94a3b8",
              cursor: "pointer",
              padding: 4,
            }}
          >
            <InlineIcon name="close" size={18} />
          </button>
        </div>

        {/* Modal Content */}
        <div
          style={{
            padding: 24,
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          <div
            style={{
              background: "#f8fafc",
              borderRadius: 12,
              padding: 16,
              border: "1px solid #f1f5f9",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                marginBottom: 4,
              }}
            >
              Credential Details
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
              {cred.title || "Academic Document"}
            </div>
            <div style={{ fontSize: 13, color: "#475569", marginTop: 4 }}>
              {[
                cred.issuer,
                labelType(cred.type),
                formatDateRange(cred.startDate, cred.endDate, cred.isCurrent),
              ]
                .filter(Boolean)
                .join(" | ")}
            </div>

            {(cred.startDate ||
              cred.endDate ||
              cred.isCurrent ||
              metaPairs.length > 0) && (
              <div
                style={{
                  marginTop: 12,
                  paddingTop: 12,
                  borderTop: "1px solid #e2e8f0",
                  fontSize: 12,
                  color: "#334155",
                }}
              >
                {cred.startDate && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      margin: "4px 0",
                    }}
                  >
                    <span style={{ color: "#64748b" }}>From Date:</span>
                    <span style={{ fontWeight: 600 }}>
                      {formatMonthYear(cred.startDate)}
                    </span>
                  </div>
                )}
                {(cred.endDate || cred.isCurrent) && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      margin: "4px 0",
                    }}
                  >
                    <span style={{ color: "#64748b" }}>End Date:</span>
                    <span style={{ fontWeight: 600 }}>
                      {cred.isCurrent
                        ? "Present"
                        : formatMonthYear(cred.endDate)}
                    </span>
                  </div>
                )}
                {metaPairs.map(([k, v]) => (
                  <div
                    key={k}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      margin: "4px 0",
                    }}
                  >
                    <span style={{ color: "#64748b" }}>{humanizeKey(k)}:</span>
                    <span style={{ fontWeight: 600 }}>{String(v)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {req.requestNote && (
            <div
              style={{
                fontSize: 13,
                color: "#334155",
                background: "#eff6ff",
                padding: 12,
                borderRadius: 8,
              }}
            >
              <strong style={{ color: "#2563eb" }}>
                Note from candidate:{" "}
              </strong>
              {req.requestNote}
            </div>
          )}

          {hasCert && (
            <button
              onClick={onOpenFile}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: "10px 16px",
                borderRadius: 8,
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                color: "#15803d",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              View Uploaded Certificate / Proof File
              <InlineIcon name="external" size={14} />
            </button>
          )}

          {req.status === "REJECTED" && req.decisionReason && (
            <div
              style={{
                fontSize: 13,
                color: "#dc2626",
                background: "#fef2f2",
                padding: 12,
                borderRadius: 8,
              }}
            >
              <strong>Rejection reason: </strong>
              {req.decisionReason}
            </div>
          )}
        </div>

        {/* Modal Actions */}
        <div
          style={{
            padding: "16px 24px",
            background: "#f8fafc",
            borderTop: "1px solid #f1f5f9",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <button
            onClick={onOpenThread}
            style={{
              padding: "8px 14px",
              fontSize: 13,
              fontWeight: 600,
              color: "#2563eb",
              background: "#ffffff",
              border: "1px solid #cbd5e1",
              borderRadius: 8,
              cursor: "pointer",
            }}
          >
            Message Candidate
          </button>

          <div style={{ display: "flex", gap: 10 }}>
            {isPending ? (
              <>
                <button
                  onClick={onReject}
                  style={{
                    padding: "9px 18px",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#dc2626",
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: 8,
                    cursor: "pointer",
                  }}
                >
                  Reject
                </button>
                <button
                  onClick={onApprove}
                  style={{
                    padding: "9px 18px",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#ffffff",
                    background: "#16a34a",
                    border: "none",
                    borderRadius: 8,
                    cursor: "pointer",
                  }}
                >
                  Approve Verification
                </button>
              </>
            ) : (
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: req.status === "VERIFIED" ? "#16a34a" : "#dc2626",
                  padding: "8px 0",
                }}
              >
                Status: {req.status}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* =============================================================================
   DecisionDialog Component
   ============================================================================= */

function DecisionDialog({
  decision,
  reason,
  setReason,
  deciding,
  error,
  onCancel,
  onConfirm,
  t,
}) {
  const { req, mode } = decision;
  const isReject = mode === "reject";
  const candidate =
    `${req.professional?.firstName ?? ""} ${req.professional?.lastName ?? ""}`.trim();
  const accent = isReject ? C.red : C.green;

  return (
    <div
      onClick={onCancel}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 300,
        background: "rgba(15, 23, 42, 0.5)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 460,
          background: C.bg,
          borderRadius: 12,
          boxShadow: "0 16px 48px rgba(0,0,0,0.22)",
          fontFamily: font,
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "16px 18px 12px" }}>
          <h2
            style={{
              fontSize: 16,
              fontWeight: 700,
              margin: "0 0 4px",
              color: C.textDark,
            }}
          >
            {isReject ? "Reject this credential?" : "Approve this credential?"}
          </h2>
          <p
            style={{
              fontSize: 13,
              color: C.textMed,
              margin: 0,
              lineHeight: 1.45,
            }}
          >
            <strong>{req.credential?.title || "Credential"}</strong>
            {candidate && <> | {candidate}</>}
            {isReject
              ? " - tell the professional why so they can fix and resubmit."
              : " - confirm verification status for institutional records."}
          </p>
        </div>

        <div style={{ padding: "0 18px 16px" }}>
          {isReject && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                marginBottom: 8,
              }}
            >
              {SUGGESTED_REJECTION_REASONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setReason(s)}
                  style={{
                    padding: "4px 10px",
                    fontSize: 11,
                    fontWeight: 500,
                    color: C.textMed,
                    background: C.bgMuted,
                    border: `1px solid ${C.divider}`,
                    borderRadius: 99,
                    cursor: "pointer",
                    fontFamily: font,
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <textarea
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={
              isReject
                ? "Reason for rejection (required)"
                : "Optional note (visible internally)"
            }
            style={{
              width: "100%",
              padding: "9px 11px",
              fontSize: 13,
              fontFamily: font,
              border: `1.5px solid ${C.dividerStrong}`,
              borderRadius: 6,
              outline: "none",
              minHeight: 80,
              resize: "vertical",
              boxSizing: "border-box",
            }}
          />

          {error && (
            <div
              style={{
                fontSize: 12,
                color: C.red,
                background: C.redLight,
                padding: "7px 10px",
                borderRadius: 4,
                marginTop: 8,
              }}
            >
              {error}
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            padding: "12px 18px",
            borderTop: `1px solid ${C.divider}`,
            background: C.bgMuted,
          }}
        >
          <button
            onClick={onCancel}
            disabled={deciding}
            style={{
              padding: "9px 18px",
              fontSize: 13,
              fontWeight: 600,
              color: C.textDark,
              background: "transparent",
              border: `1px solid ${C.dividerStrong}`,
              borderRadius: 6,
              cursor: deciding ? "default" : "pointer",
              fontFamily: font,
            }}
          >
            {t("common.cancel")}
          </button>
          <button
            onClick={onConfirm}
            disabled={deciding}
            style={{
              padding: "9px 18px",
              fontSize: 13,
              fontWeight: 600,
              color: "white",
              background: deciding ? C.textLight : accent,
              border: 0,
              borderRadius: 6,
              cursor: deciding ? "default" : "pointer",
              fontFamily: font,
            }}
          >
            {deciding ? "Submitting..." : isReject ? "Reject" : "Approve"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MarkEndedDialog({
  req,
  endDate,
  setEndDate,
  busy,
  error,
  onCancel,
  onConfirm,
}) {
  const fullName =
    `${req.professional?.firstName ?? ""} ${req.professional?.lastName ?? ""}`.trim() ||
    "this employee";
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 300,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        style={{
          background: C.bg,
          borderRadius: 10,
          padding: 24,
          width: "min(420px, 100%)",
          boxShadow: "0 16px 48px rgba(0,0,0,0.22)",
          fontFamily: font,
        }}
      >
        <h2
          style={{
            fontSize: 17,
            fontWeight: 700,
            margin: "0 0 6px",
            color: C.textDark,
          }}
        >
          Mark employment as ended
        </h2>
        <p
          style={{
            fontSize: 13,
            color: C.textMed,
            lineHeight: 1.55,
            margin: "0 0 16px",
          }}
        >
          Record the last working day of <strong>{fullName}</strong> for{" "}
          <strong>{req.credential?.title}</strong>. Their profile will show the
          employment as ended, confirmed by your organisation. They'll be
          notified.
        </p>
        <label
          style={{
            display: "block",
            fontSize: 12,
            fontWeight: 600,
            color: C.textDark,
            marginBottom: 4,
          }}
        >
          Last working day
        </label>
        <input
          type="date"
          value={endDate}
          max={today}
          onChange={(e) => setEndDate(e.target.value)}
          style={{
            width: "100%",
            padding: "9px 10px",
            fontSize: 14,
            fontFamily: font,
            border: `1px solid ${C.dividerStrong}`,
            borderRadius: 6,
            color: C.textDark,
            marginBottom: 12,
          }}
        />
        {error && (
          <div
            style={{
              fontSize: 12,
              color: C.red,
              background: C.redLight,
              padding: "8px 10px",
              borderRadius: 6,
              marginBottom: 12,
            }}
          >
            {error}
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            onClick={onCancel}
            disabled={busy}
            style={{
              padding: "9px 18px",
              fontSize: 13,
              fontWeight: 600,
              color: C.textMed,
              background: "transparent",
              border: `1px solid ${C.dividerStrong}`,
              borderRadius: 6,
              cursor: "pointer",
              fontFamily: font,
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            style={{
              padding: "9px 18px",
              fontSize: 13,
              fontWeight: 700,
              color: "#fff",
              background: busy ? C.textLight : C.navy,
              border: 0,
              borderRadius: 6,
              cursor: busy ? "default" : "pointer",
              fontFamily: font,
            }}
          >
            {busy ? "Saving..." : "Confirm end date"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Helpers
function getInitials(name) {
  if (!name) return "AP";
  const parts = name.trim().split(" ");
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function formatInboxItemMeta(req) {
  const isDecided = req.status === "VERIFIED" || req.status === "REJECTED";
  const prefix = isDecided ? "Reviewed" : "Submitted";
  const dateStr = isDecided
    ? req.decidedAt || req.updatedAt || req.createdAt
    : req.createdAt;
  if (!dateStr) return `${prefix} recently`;

  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return `${prefix} recently`;

  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  const timeString = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  if (isToday) {
    return `${prefix} today, ${timeString}`;
  }
  if (isYesterday) {
    return `${prefix} yesterday, ${timeString}`;
  }
  return `${prefix} on ${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
}

function metadataPairs(metadata) {
  if (!metadata || typeof metadata !== "object") return [];
  const excluded = new Set([
    "startDate",
    "endDate",
    "fromDate",
    "toDate",
    "isCurrent",
  ]);
  return Object.entries(metadata).filter(
    ([k, v]) => !excluded.has(k) && v !== null && v !== "" && v !== undefined,
  );
}

function humanizeKey(key) {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .replace(/Id$/, "ID");
}

function labelType(type) {
  return (
    { EDUCATION: "Education", WORK: "Work experience", OTHER: "Other" }[type] ??
    type
  );
}

function formatMonthYear(dateVal) {
  if (!dateVal) return "";
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return String(dateVal);
  return d.toLocaleDateString("en-IN", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatDateRange(start, end, isCurrent) {
  const s = formatMonthYear(start);
  const e = isCurrent ? "Present" : formatMonthYear(end);
  if (s && e) return `${s} - ${e}`;
  return s || e || "";
}
